/* SKU ordering uses the current storefront session. No prices or customer records are persisted. */
(function (root, factory) {
  const api = factory(root);
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root && root.document) root.SaberQuickOrder = api;
})(typeof window === "undefined" ? globalThis : window, function (root) {
  "use strict";

  const MAX_ROWS = 100;
  const catalogSearch = () => {
    if (root.SaberCatalogSearch) return root.SaberCatalogSearch;
    if (typeof module === "object" && module.exports)
      return require("./saber-catalog-search.js");
    throw new Error(
      "Product matching could not be loaded. Refresh the page and try again.",
    );
  };
  const normalizeSku = (value) =>
    String(value || "")
      .trim()
      .toLocaleUpperCase("en-US");
  const positiveInteger = (value) =>
    /^\d+$/.test(String(value).trim()) &&
    Number.isSafeInteger(Number(value)) &&
    Number(value) > 0;
  const idString = (value) =>
    /^\d+$/.test(String(value || "")) ? String(value) : "";

  function delimiterFor(text) {
    let quoted = false,
      fieldStart = true;
    for (let i = 0; i < text.length; i += 1) {
      if (text[i] === '"' && (quoted || fieldStart)) {
        if (quoted && text[i + 1] === '"') i += 1;
        else quoted = !quoted;
      }
      if (!quoted && text[i] === "\t") return "\t";
      if (!quoted && /[\r\n]/.test(text[i])) break;
      if (!quoted && text[i] === ",") fieldStart = true;
      else if (!/\s/.test(text[i])) fieldStart = false;
    }
    return ",";
  }

  function parseRows(input) {
    const text = String(input || "").replace(/^\uFEFF/, "");
    const delimiter = delimiterFor(text);
    const records = [];
    let fields = [],
      field = "",
      quoted = false,
      closed = false,
      error = "",
      line = 1,
      startLine = 1;
    function finishField() {
      fields.push(field.trim());
      field = "";
      closed = false;
    }
    function finishRow() {
      finishField();
      if (fields.some((value) => value !== "") || error)
        records.push({ fields, line: startLine, error });
      fields = [];
      error = "";
      startLine = line + 1;
    }
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      if (quoted) {
        if (ch === '"' && text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else if (ch === '"') {
          quoted = false;
          closed = true;
        } else {
          field += ch;
          if (ch === "\n") line += 1;
        }
      } else if (ch === '"' && field.trim() === "" && !closed) {
        quoted = true;
        field = "";
      } else if (ch === delimiter) {
        finishField();
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i += 1;
        finishRow();
        line += 1;
      } else if (closed && !/\s/.test(ch)) {
        error = "Unexpected text after a quoted field.";
        field += ch;
      } else field += ch;
    }
    if (quoted) error = "A quoted field is missing its closing double quote.";
    if (field || fields.length || error) finishRow();
    if (
      records.length &&
      /^(sku|product\s*sku|item\s*sku)$/i.test(records[0].fields[0]) &&
      (records[0].fields.length === 1 ||
        /^(quantity|qty)$/i.test(records[0].fields[1]))
    )
      records.shift();
    const result = records.slice(0, MAX_ROWS).map((record) => {
      const sku = record.fields[0] || "";
      const rawQuantity = record.fields.length === 1 ? "1" : record.fields[1];
      let rowError = record.error;
      if (record.fields.length > 2)
        rowError =
          "Use two columns: SKU and quantity. Put a SKU containing commas in double quotes.";
      else if (!sku) rowError = "Enter a SKU.";
      else if (!positiveInteger(rawQuantity))
        rowError = "Quantity must be a whole number greater than zero.";
      return {
        sku,
        quantity: positiveInteger(rawQuantity)
          ? Number(rawQuantity)
          : rawQuantity,
        line: record.line,
        error: rowError || "",
      };
    });
    if (records.length > MAX_ROWS)
      result.push({
        sku: "",
        quantity: 0,
        line: records[MAX_ROWS].line,
        error:
          "This list has more than 100 products. Split it into smaller lists.",
      });
    return result;
  }

  function candidatesFrom(payload) {
    if (!payload || !Array.isArray(payload.products))
      throw new Error(
        "The catalog response was unavailable. Please try reviewing again.",
      );
    return payload.products.flatMap((product) =>
      [
        ...(Array.isArray(product.variants) ? product.variants : []),
        ...(product.selectedVariant ? [product.selectedVariant] : []),
      ]
        .filter((variant) => idString(variant.id))
        .map((variant) => ({
          ...variant,
          id: String(variant.id),
          sku: String(variant.sku || ""),
          productTitle: String(product.title || "Product"),
          variantTitle:
            variant.title === "Default Title"
              ? ""
              : String(variant.title || ""),
          url: String(product.url || ""),
          image: product.image || "",
          requiresSellingPlan: product.requiresSellingPlan === true,
          min: positiveInteger(variant.min) ? Number(variant.min) : 1,
          increment: positiveInteger(variant.increment)
            ? Number(variant.increment)
            : 1,
          max: positiveInteger(variant.max) ? Number(variant.max) : null,
        })),
    );
  }

  function matchProducts(payload, item, byId) {
    const all = candidatesFrom(payload);
    const seen = new Set();
    const exactMatches = all.filter((variant) => {
      const exact = byId
        ? variant.id === idString(item.variantId)
        : catalogSearch().isExactSkuMatch(variant.sku, item.sku);
      if (!exact || seen.has(variant.id)) return false;
      seen.add(variant.id);
      return true;
    });
    const suggested = !byId && exactMatches.length === 0;
    const matches = suggested
      ? all.filter((variant) => {
          if (seen.has(variant.id)) return false;
          const match = catalogSearch().isSkuPrefixMatch(variant.sku, item.sku);
          if (match) seen.add(variant.id);
          return match;
        })
      : exactMatches;
    const incomplete =
      !byId &&
      (payload.truncated === true ||
        payload.products.some((product) => product.variantsTruncated === true));
    let error = "";
    if (incomplete)
      error =
        "The catalog returned a limited result set. Open the product to confirm its SKU, or narrow your entry before ordering.";
    else if (!matches.length)
      error = byId
        ? "This saved product option is no longer available in your catalog."
        : "No matching item number, model or SKU. Check your entry or search for the product.";
    return {
      ...item,
      matches,
      selectedId:
        !error && !suggested && matches.length === 1 ? matches[0].id : "",
      requiresSelection: suggested || matches.length > 1,
      suggested,
      incomplete,
      error,
    };
  }

  function productLookupUrl(item, origin) {
    if (!idString(item.variantId) || !item.url) return null;
    const url = new URL(item.url, origin);
    if (url.origin !== origin || !/\/products\/[^/]+\/?$/.test(url.pathname))
      throw new Error("Use a product link from this store for saved products.");
    url.search = "";
    url.hash = "";
    url.searchParams.set("view", "quick-order");
    url.searchParams.set("variant", idString(item.variantId));
    return url;
  }

  async function resolveRows(items, options = {}) {
    if (!Array.isArray(items) || items.length > MAX_ROWS)
      throw new Error("Review up to 100 products at a time.");
    const origin =
      options.origin || root.location?.origin || "https://store.invalid";
    const fetcher = options.fetch || root.fetch.bind(root);
    const signal = options.signal;
    const jobs = new Map();
    const rows = items
      .slice(0, MAX_ROWS + 1)
      .map((item, index) => ({ ...item, line: item.line || index + 1 }));
    rows.forEach((item) => {
      if (item.error) return;
      if (!positiveInteger(item.quantity)) {
        item.error = "Quantity must be a whole number greater than zero.";
        return;
      }
      try {
        const productUrl = productLookupUrl(item, origin);
        if (!productUrl && !normalizeSku(item.sku)) {
          item.error = "A SKU or a valid saved product reference is required.";
          return;
        }
        const url =
          productUrl || new URL(options.searchUrl || "/search", origin);
        if (!productUrl) {
          url.searchParams.set("view", "quick-order");
          url.searchParams.set("type", "product");
          url.searchParams.set("options[prefix]", "last");
          url.searchParams.set("q", catalogSearch().buildSearchQuery(item.sku));
        }
        item.lookupKey = url.href;
        item.byId = !!productUrl;
        if (!jobs.has(url.href))
          jobs.set(url.href, {
            url: url.href,
            sku: item.sku,
            byId: !!productUrl,
          });
      } catch (error) {
        item.error = error.message;
      }
    });
    const queue = Array.from(jobs.values());
    let cursor = 0;
    async function fetchPayload(url) {
      const response = await fetcher(url, {
        signal,
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!response.ok)
        throw new Error(
          "Catalog lookup failed. Review again or open the product page.",
        );
      let payload;
      try {
        payload = await response.json();
      } catch (_) {
        throw new Error(
          "The catalog response could not be read. Refresh this page and try reviewing again, or open product search.",
        );
      }
      candidatesFrom(payload);
      return payload;
    }
    async function worker() {
      while (cursor < queue.length) {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        const job = queue[cursor++];
        try {
          job.payload = await fetchPayload(job.url);
          if (
            !job.byId &&
            !candidatesFrom(job.payload).some((variant) =>
              catalogSearch().isExactSkuMatch(variant.sku, job.sku),
            )
          ) {
            const prefixes = catalogSearch().prefixQueries(job.sku);
            const narrowed = [];
            for (const query of prefixes) {
              const url = new URL(job.url);
              url.searchParams.set("q", query);
              narrowed.push(await fetchPayload(url.href));
            }
            if (
              narrowed.some((payload) =>
                candidatesFrom(payload).some((variant) =>
                  catalogSearch().isSkuPrefixMatch(variant.sku, job.sku),
                ),
              )
            ) {
              const products = new Map();
              narrowed.forEach((payload) =>
                payload.products.forEach((product) => {
                  const key = String(product.id || product.url);
                  if (!products.has(key)) products.set(key, product);
                }),
              );
              job.payload = {
                products: [...products.values()],
                truncated: narrowed.some(
                  (payload) => payload.truncated === true,
                ),
              };
            }
          }
        } catch (error) {
          if (signal?.aborted || error.name === "AbortError") throw error;
          job.error = error.message;
        }
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(3, queue.length) }, worker),
    );
    return rows.map((row) => {
      if (row.error) return { ...row, matches: [], selectedId: "" };
      const job = jobs.get(row.lookupKey);
      if (job.error)
        return { ...row, matches: [], selectedId: "", error: job.error };
      return matchProducts(job.payload, row, row.byId);
    });
  }

  function selectedVariant(row) {
    return row.matches?.find(
      (variant) => variant.id === String(row.selectedId),
    );
  }

  function aggregateRows(rows) {
    const grouped = new Map(),
      errors = [];
    rows.forEach((row) => {
      const variant = selectedVariant(row);
      if (row.error || row.incomplete)
        errors.push(`Row ${row.line}: ${row.error || "Review this product."}`);
      else if (!variant)
        errors.push(`Row ${row.line}: choose the correct product option.`);
      else if (!variant.available)
        errors.push(`Row ${row.line}: this option is currently unavailable.`);
      else if (variant.orderable === false)
        errors.push(
          `Row ${row.line}: ${variant.orderBlockReason || "Open this product page to order."}`,
        );
      else if (variant.requiresSellingPlan)
        errors.push(
          `Row ${row.line}: choose a purchase option on the product page.`,
        );
      else if (!positiveInteger(row.quantity))
        errors.push(
          `Row ${row.line}: enter a whole quantity greater than zero.`,
        );
      else {
        const existing = grouped.get(variant.id);
        const quantity = Number(row.quantity) + (existing?.quantity || 0);
        if (!Number.isSafeInteger(quantity))
          errors.push(`Row ${row.line}: the combined quantity is too large.`);
        else grouped.set(variant.id, { ...variant, quantity });
      }
    });
    return { items: [...grouped.values()], errors };
  }

  function cartQuantities(cart) {
    if (!cart || !Array.isArray(cart.items))
      throw new Error(
        "Your cart could not be checked. Please open your cart before ordering.",
      );
    return cart.items.reduce((totals, item) => {
      const id = String(item.variant_id || item.id);
      totals[id] = (totals[id] || 0) + Number(item.quantity || 0);
      return totals;
    }, {});
  }

  function validateAggregate(items, cart) {
    const totals = cartQuantities(cart);
    return items.flatMap((item) => {
      const finalQuantity = item.quantity + (totals[item.id] || 0);
      const name = item.sku || item.productTitle;
      if (finalQuantity < item.min)
        return [
          `${name}: minimum ${item.min} total in cart; you requested ${item.quantity}.`,
        ];
      if (item.max && finalQuantity > item.max)
        return [
          `${name}: maximum ${item.max} total in cart, including ${totals[item.id] || 0} already there.`,
        ];
      if (finalQuantity % item.increment !== 0)
        return [
          `${name}: total cart quantity must be in multiples of ${item.increment}.`,
        ];
      return [];
    });
  }

  function reconcile(items, before, after) {
    const previous = cartQuantities(before),
      current = cartQuantities(after);
    return items.map((item) => {
      const observed = Math.max(
        0,
        (current[item.id] || 0) - (previous[item.id] || 0),
      );
      return {
        ...item,
        added: Math.min(item.quantity, observed),
        remaining: Math.max(0, item.quantity - observed),
      };
    });
  }

  const csvItems = (items) =>
    items
      .map(
        (item) =>
          '"' +
          String(item.sku || "").replace(/"/g, '""') +
          '",' +
          item.quantity,
      )
      .join("\n");

  class QuickOrder extends (root.HTMLElement || class {}) {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.rows = [];
      this.completed = false;
      this.uncertain = false;
      this.input = this.querySelector("[data-qo-input]");
      this.rowsElement = this.querySelector("[data-qo-rows]");
      this.reviewButton = this.querySelector("[data-qo-review]");
      this.addButton = this.querySelector("[data-qo-add]");
      this.saveButton = this.querySelector("[data-qo-save]");
      this.statusElement = this.querySelector("[data-qo-status]");
      this.singleSku = this.querySelector("[data-qo-single-sku]");
      this.singleQuantity = this.querySelector("[data-qo-single-quantity]");
      this.singleButton = this.querySelector("[data-qo-single-add]");
      this.fileInput = this.querySelector("[data-qo-file]");
      this.pastePanel = this.querySelector("[data-qo-paste-panel]");
      this.singleButton?.addEventListener("click", () =>
        this.reviewSingleItem(),
      );
      [this.singleSku, this.singleQuantity].forEach((field) => {
        field?.addEventListener("keydown", (event) => {
          if (event.key === "Enter" && !event.isComposing) {
            event.preventDefault();
            this.reviewSingleItem();
          }
        });
      });
      this.fileInput?.addEventListener("change", () => this.importFile());
      this.reviewButton.addEventListener("click", () =>
        this.reviewItems(this.stagedItems || parseRows(this.input.value)),
      );
      this.input.addEventListener("input", () => {
        if (this.adding || this.uncertain) return;
        this.controller?.abort();
        this.fileSequence = (this.fileSequence || 0) + 1;
        this.rows = [];
        this.completed = false;
        this.stagedItems = null;
        this.render();
        this.setStatus(
          "Your list changed. Review products again before adding.",
        );
      });
      this.addButton?.addEventListener("click", () => this.addItems());
      this.saveButton.addEventListener("click", () => this.saveItems());
      this.onItems = (event) => {
        if (Array.isArray(event.detail?.items))
          this.reviewItems(event.detail.items);
      };
      root.document.addEventListener("saber:quick-order-items", this.onItems);
      this.onSavedReady = () => this.consumeStaged();
      root.document.addEventListener("savedlists:ready", this.onSavedReady);
      root.addEventListener("hashchange", this.onSavedReady);
      this.consumeStaged();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.fileSequence = (this.fileSequence || 0) + 1;
      root.document.removeEventListener(
        "saber:quick-order-items",
        this.onItems,
      );
      root.document.removeEventListener("savedlists:ready", this.onSavedReady);
      root.removeEventListener("hashchange", this.onSavedReady);
      this.initialized = false;
    }

    entryItems() {
      if (this.completed) return [];
      if (!this.rows.length)
        return this.stagedItems || parseRows(this.input.value);
      return this.rows.map((row) => {
        const variant = selectedVariant(row);
        return {
          sku: row.sku,
          quantity: row.quantity,
          line: row.line,
          ...(variant
            ? { variantId: variant.id, url: variant.url }
            : row.variantId
              ? { variantId: row.variantId, url: row.url }
              : {}),
          ...(/Quantity must|quoted|two columns|Enter a SKU/i.test(
            row.error || "",
          )
            ? { error: row.error }
            : {}),
        };
      });
    }

    reviewSingleItem() {
      if (this.adding || this.uncertain) return;
      const sku = this.singleSku?.value.trim() || "";
      const quantity = this.singleQuantity?.value || "";
      if (!sku) {
        this.setStatus("Enter an item number, model or full SKU.", true);
        this.singleSku?.focus();
        return;
      }
      if (!positiveInteger(quantity)) {
        this.setStatus(
          "Quantity must be a whole number greater than zero.",
          true,
        );
        this.singleQuantity?.focus();
        return;
      }
      const items = this.entryItems();
      if (items.length >= MAX_ROWS) {
        this.setStatus(
          "This list already has 100 rows. Add it to your cart or save it before starting another list.",
          true,
        );
        return;
      }
      this.reviewItems([
        ...items,
        {
          sku,
          quantity: Number(quantity),
          line: Math.max(0, ...items.map((item) => Number(item.line) || 0)) + 1,
        },
      ]);
      this.singleSku.value = "";
      this.singleQuantity.value = "1";
      this.singleSku.focus();
    }

    async importFile() {
      if (this.adding || this.uncertain) return;
      const file = this.fileInput?.files?.[0];
      if (!file) return;
      const sequence = (this.fileSequence || 0) + 1;
      this.fileSequence = sequence;
      if (file.size > 128 * 1024) {
        this.setStatus(
          "Choose a CSV or TSV file smaller than 128 KB, with up to 100 rows.",
          true,
        );
        this.fileInput.value = "";
        return;
      }
      try {
        const text = await file.text();
        if (!this.isConnected || sequence !== this.fileSequence) return;
        this.input.value = text;
        this.input.dispatchEvent(new Event("input", { bubbles: true }));
        if (this.pastePanel) this.pastePanel.open = true;
        this.setStatus(
          "Your file is loaded. Review the product list to find matching items before adding.",
        );
        this.reviewButton.focus();
      } catch (_) {
        this.setStatus(
          "This file could not be read. Paste your rows or choose a CSV or TSV file.",
          true,
        );
      } finally {
        if (this.fileInput) this.fileInput.value = "";
      }
    }

    removeRow(index) {
      if (this.adding || this.completed || this.uncertain) return;
      const items = this.entryItems();
      this.rows.splice(index, 1);
      items.splice(index, 1);
      this.stagedItems = items;
      this.input.value = csvItems(items);
      this.render();
      this.setStatus(
        this.rows.length
          ? "Item removed. Review the remaining products and quantities before adding."
          : "Your list is empty. Enter an item above or paste a new product list.",
      );
      this.singleSku?.focus();
    }

    consumeStaged() {
      if (
        root.location.hash !== "#from-saved-list" ||
        !root.SaberSavedLists?.consumeStagedItems
      )
        return;
      const items = root.SaberSavedLists.consumeStagedItems();
      if (Array.isArray(items) && items.length) this.reviewItems(items);
      else
        this.setStatus(
          "No saved list is available for this account or location. Open your purchasing lists and review the list again.",
          true,
        );
    }

    setStatus(message, error = false) {
      this.statusElement.textContent = message;
      this.statusElement.dataset.error = String(error);
    }

    async reviewItems(items) {
      if (this.adding || this.uncertain) return;
      this.controller?.abort();
      this.fileSequence = (this.fileSequence || 0) + 1;
      const controller = new AbortController();
      this.controller = controller;
      this.rows = [];
      this.completed = false;
      this.render();
      if (!Array.isArray(items) || !items.length) {
        this.setStatus("Enter at least one SKU to review.", true);
        return;
      }
      if (items.length > MAX_ROWS) {
        this.setStatus(
          "Review up to 100 products at a time. Split your list into smaller orders.",
          true,
        );
        return;
      }
      this.stagedItems = items.map((item) => ({ ...item }));
      this.input.value = csvItems(items);
      this.setAttribute("aria-busy", "true");
      this.setStatus("Matching your SKUs against the live catalog…");
      try {
        const rows = await resolveRows(items, {
          searchUrl: this.dataset.searchUrl,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        this.rows = rows;
        this.render();
        const { errors } = aggregateRows(rows);
        this.setStatus(
          errors.length
            ? "Review the marked rows. Every product must be resolved before the list can be added."
            : "Products matched. Check quantities, then add your reviewed items together.",
          !!errors.length,
        );
      } catch (error) {
        if (!controller.signal.aborted)
          this.setStatus(
            error.message ||
              "Catalog lookup was unavailable. Please review again.",
            true,
          );
      } finally {
        if (this.controller === controller) this.removeAttribute("aria-busy");
      }
    }

    reviewedItems() {
      const { items, errors } = aggregateRows(this.rows);
      if (errors.length) return [];
      return items.map((item) => ({
        sku: item.sku,
        quantity: item.quantity,
        variantId: item.id,
        title: item.productTitle,
        url: item.url,
      }));
    }

    saveItems() {
      const items = this.reviewedItems();
      if (!items.length) return;
      if (root.SaberSavedLists?.openSaveDialog) {
        try {
          root.SaberSavedLists.openSaveDialog(items);
        } catch (error) {
          this.setStatus(
            error.message ||
              "This list could not be saved. Please review the quantities.",
            true,
          );
        }
      } else {
        const event = new CustomEvent("saber:save-list", {
          bubbles: true,
          cancelable: true,
          detail: { items, source: "quick-order" },
        });
        if (this.dispatchEvent(event))
          this.setStatus(
            "Purchasing lists could not be opened. Refresh the page and try again.",
            true,
          );
      }
    }

    element(tag, className, text) {
      const node = root.document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    safeUrl(value, image = false) {
      if (typeof value !== "string" || !value.trim()) return "";
      try {
        const url = new URL(value, root.location.origin);
        return /^(https?:)$/.test(url.protocol) &&
          (image || url.origin === root.location.origin)
          ? url.href
          : "";
      } catch (_) {
        return "";
      }
    }

    variantUrl(variant) {
      const safe = this.safeUrl(variant.url);
      if (!safe) return this.dataset.searchUrl;
      const url = new URL(safe);
      url.searchParams.set("variant", variant.id);
      return url.href;
    }

    render() {
      this.rowsElement.replaceChildren();
      this.querySelector("[data-qo-empty]").hidden = this.rows.length > 0;
      this.querySelector("[data-qo-actions]").hidden = this.rows.length === 0;
      this.querySelector("[data-qo-count]").textContent = this.rows.length
        ? `${this.rows.length} rows`
        : "";
      this.rows.forEach((row, index) => {
        const article = this.element("article", "ss-qo-row");
        const main = this.element("div");
        main.append(
          this.element(
            "p",
            "ss-qo-row-meta",
            `Row ${row.line} · SKU: ${row.sku || "Saved product"}`,
          ),
        );
        if (
          (row.requiresSelection || row.matches?.length > 1) &&
          row.matches?.length &&
          !row.incomplete
        ) {
          const selectId = `qo-match-${this.id || "main"}-${index}`;
          const label = this.element(
            "label",
            "",
            row.suggested
              ? "Choose the product you meant"
              : "Choose the exact product option",
          );
          label.htmlFor = selectId;
          const select = this.element("select");
          select.id = selectId;
          const empty = this.element(
            "option",
            "",
            "Select a matching product…",
          );
          empty.value = "";
          select.append(empty);
          row.matches.forEach((variant) => {
            const option = this.element(
              "option",
              "",
              `${variant.productTitle}${variant.variantTitle ? " / " + variant.variantTitle : ""}${variant.sku ? " — " + variant.sku : ""} — ${variant.priceText || "View price"}${variant.available ? "" : " — unavailable"}`,
            );
            option.value = variant.id;
            option.selected = row.selectedId === variant.id;
            select.append(option);
          });
          select.disabled = !!this.adding || this.completed;
          select.addEventListener("change", () => {
            row.selectedId = select.value;
            this.render();
            root.document.getElementById(selectId)?.focus();
            const { errors } = aggregateRows(this.rows);
            this.setStatus(
              errors.length
                ? "Review the marked rows before adding your list."
                : "Products matched. Check quantities, then add your reviewed items together.",
              !!errors.length,
            );
          });
          main.append(label, select);
        }
        const variant = selectedVariant(row);
        if (variant) {
          const product = this.element("div", "ss-qo-row-product");
          const imageUrl = this.safeUrl(variant.image, true);
          if (imageUrl) {
            const image = this.element("img");
            image.src = imageUrl;
            image.alt = "";
            image.width = 72;
            image.height = 72;
            image.loading = "lazy";
            image.addEventListener(
              "error",
              () => {
                const placeholder = this.element(
                  "span",
                  "ss-qo-row-placeholder",
                  "▧",
                );
                placeholder.setAttribute("aria-hidden", "true");
                image.replaceWith(placeholder);
              },
              { once: true },
            );
            product.append(image);
          } else {
            const placeholder = this.element(
              "span",
              "ss-qo-row-placeholder",
              "▧",
            );
            placeholder.setAttribute("aria-hidden", "true");
            product.append(placeholder);
          }
          const detail = this.element("div");
          const title = this.element(
            "a",
            "ss-qo-row-title",
            variant.productTitle,
          );
          title.href = this.variantUrl(variant);
          detail.append(title);
          if (
            variant.sku &&
            normalizeSku(variant.sku) !== normalizeSku(row.sku)
          )
            detail.append(
              this.element("p", "ss-qo-row-meta", `SKU: ${variant.sku}`),
            );
          if (variant.variantTitle)
            detail.append(
              this.element("p", "ss-qo-row-meta", variant.variantTitle),
            );
          const price = this.element(
            "p",
            "ss-qo-row-price",
            variant.priceText || "See product price",
          );
          if (
            typeof variant.price === "number" &&
            variant.price >= 0 &&
            variant.priceLabel === "Your Price"
          )
            price.prepend(this.element("span", "ss-your-price", "Your Price"));
          detail.append(price);
          if (variant.priceBreaks?.length)
            detail.append(
              this.element(
                "p",
                "ss-qo-row-rule",
                "Base unit price shown. Quantity pricing: " +
                  variant.priceBreaks
                    .map(
                      (tier) =>
                        `${tier.quantity}+ units: ${tier.priceText} each`,
                    )
                    .join(" · ") +
                  ". Your cart confirms the applicable price.",
              ),
            );
          detail.append(
            this.element(
              "p",
              variant.available ? "ss-qo-row-availability" : "ss-qo-row-error",
              variant.available
                ? variant.orderable === false
                  ? "Order through the product page"
                  : "Available to order"
                : "Currently unavailable",
            ),
          );
          detail.append(
            this.element(
              "p",
              "ss-qo-row-rule",
              `Minimum ${variant.min} · Multiples of ${variant.increment}${variant.max ? " · Maximum " + variant.max : ""}`,
            ),
          );
          if (variant.requiresSellingPlan)
            detail.append(
              this.element(
                "p",
                "ss-qo-row-error",
                "Choose a purchase option on the product page.",
              ),
            );
          if (variant.orderable === false) {
            detail.append(
              this.element(
                "p",
                "ss-qo-row-error",
                variant.orderBlockReason ||
                  "Review the ordering details on this product page.",
              ),
            );
            const productLink = this.element(
              "a",
              "ss-qo-row-link",
              "View product & ordering options",
            );
            productLink.href = this.variantUrl(variant);
            detail.append(productLink);
          }
          product.append(detail);
          main.append(product);
        }
        if (row.error)
          main.append(this.element("p", "ss-qo-row-error", row.error));
        if (!row.selectedId && row.matches?.length && !row.incomplete)
          main.append(
            this.element(
              "p",
              "ss-qo-row-error",
              row.suggested
                ? "Select the exact product above. A partial number is never added automatically."
                : "More than one option matches this identifier. Choose the one you need.",
            ),
          );
        if (row.error || !variant) {
          const link = this.element("a", "ss-qo-row-link", "Search this SKU");
          const url = new URL(this.dataset.searchUrl, root.location.origin);
          url.searchParams.set("q", row.sku || "");
          url.searchParams.set("type", "product");
          link.href = url.href;
          main.append(link);
        }
        const quantityWrap = this.element("div", "ss-qo-row-quantity");
        const quantityId = `qo-qty-${this.id || "main"}-${index}`;
        const quantityLabel = this.element("label", "", "Quantity");
        quantityLabel.htmlFor = quantityId;
        const quantity = this.element("input");
        quantity.id = quantityId;
        quantity.type = "number";
        quantity.min = "1";
        quantity.step = "1";
        quantity.inputMode = "numeric";
        quantity.value = row.quantity;
        quantity.disabled = !!this.adding || this.completed;
        quantity.addEventListener("input", () => {
          row.quantity = quantity.value;
          const quantityError = /Quantity must/.test(row.error || "");
          if (quantityError && positiveInteger(quantity.value)) row.error = "";
          if (this.stagedItems?.[index]) {
            this.stagedItems[index].quantity = quantity.value;
            if (quantityError && positiveInteger(quantity.value))
              this.stagedItems[index].error = "";
            this.input.value = csvItems(this.stagedItems);
          }
          if (!row.matches?.length && quantityError)
            this.setStatus(
              "Quantity updated. Review products again to match this row against the live catalog.",
            );
          this.updateActions();
        });
        quantityWrap.append(quantityLabel, quantity);
        const remove = this.element("button", "ss-qo-row-remove", "×");
        remove.type = "button";
        remove.setAttribute("aria-label", `Remove row ${row.line}`);
        remove.disabled = !!this.adding || this.completed || this.uncertain;
        remove.addEventListener("click", () => this.removeRow(index));
        article.append(main, quantityWrap, remove);
        this.rowsElement.append(article);
      });
      this.updateActions();
    }

    updateActions() {
      const { items, errors } = aggregateRows(this.rows);
      const ready = this.rows.length > 0 && errors.length === 0;
      if (this.addButton)
        this.addButton.disabled =
          !ready || !!this.adding || this.completed || this.uncertain;
      this.saveButton.disabled = !ready || !!this.adding;
      this.reviewButton.disabled = !!this.adding || this.uncertain;
      this.input.disabled = !!this.adding || this.uncertain;
      [
        this.singleSku,
        this.singleQuantity,
        this.singleButton,
        this.fileInput,
      ].forEach((field) => {
        if (field) field.disabled = !!this.adding || this.uncertain;
      });
      this.querySelector("[data-qo-summary]").textContent = ready
        ? `${items.length} unique product options · ${items.reduce((sum, item) => sum + item.quantity, 0)} units to add`
        : "Resolve every marked row before adding your list.";
    }

    async readCart() {
      const response = await root.fetch(this.dataset.cartUrl + ".js", {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!response.ok)
        throw new Error(
          "Your cart could not be checked. Open your cart and try again.",
        );
      const cart = await response.json();
      cartQuantities(cart);
      return cart;
    }

    updateNativeCart(response, cart) {
      const minicart = root.document.querySelector("mini-cart");
      try {
        if (response?.sections && minicart?.renderContents)
          minicart.renderContents(response);
        else minicart?.updateSectionContents?.();
      } catch (_) {
        /* The cart link still opens the authoritative full cart. */
      }
      root.document.dispatchEvent(
        new CustomEvent("cart:item-add", {
          detail: { items: response?.items || [], context: "QuickOrder", cart },
        }),
      );
      const cartEvents =
        typeof PUB_SUB_EVENTS !== "undefined"
          ? PUB_SUB_EVENTS
          : root.PUB_SUB_EVENTS;
      if (root.publish && cartEvents?.cartItemAdd)
        root.publish(cartEvents.cartItemAdd, {
          source: "QuickOrder",
          items: response?.items || [],
        });
      root.document.dispatchEvent(
        new CustomEvent("saber:cart-updated", { detail: { cart } }),
      );
    }

    async addItems() {
      if (
        this.adding ||
        this.completed ||
        this.uncertain ||
        this.dataset.catalogOnly === "true"
      )
        return;
      const { items, errors } = aggregateRows(this.rows);
      if (errors.length || !items.length) return;
      this.adding = true;
      this.setAttribute("aria-busy", "true");
      this.render();
      this.setStatus("Checking your cart and adding your reviewed products…");
      let before,
        result,
        attempted = false;
      try {
        before = await this.readCart();
        const quantityErrors = validateAggregate(items, before);
        if (quantityErrors.length) throw new Error(quantityErrors.join(" "));
        const minicart = root.document.querySelector("mini-cart");
        const body = {
          items: items.map((item) => ({
            id: item.id,
            quantity: item.quantity,
          })),
        };
        if (minicart?.getSectionsToRender) {
          body.sections = [
            ...new Set(
              minicart
                .getSectionsToRender()
                .map((section) => section.section)
                .filter(Boolean),
            ),
          ].slice(0, 5);
          body.sections_url = root.location.pathname;
        }
        attempted = true;
        const response = await root.fetch(this.dataset.cartAddUrl + ".js", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });
        result = await response.json();
        if (!response.ok || result.status)
          throw new Error(
            typeof result.description === "string"
              ? result.description
              : "Some products could not be added. Your cart will be checked before you try again.",
          );
        const after = await this.readCart();
        const progress = reconcile(items, before, after);
        if (progress.some((item) => item.remaining > 0))
          throw new Error(
            "Not all requested quantities were added. Your cart has been checked.",
          );
        this.completed = true;
        this.input.value = "";
        this.updateNativeCart(result, after);
        this.setStatus(
          `Your reviewed products are in your cart. ${progress.reduce((sum, item) => sum + item.added, 0)} units added. Review your cart to continue to checkout.`,
        );
      } catch (error) {
        if (!attempted) this.setStatus(error.message, true);
        else {
          try {
            const after = await this.readCart();
            const progress = reconcile(items, before, after);
            const remaining = progress
              .filter((item) => item.remaining > 0)
              .map((item) => ({
                sku: item.sku,
                quantity: item.remaining,
                variantId: item.id,
                title: item.productTitle,
                url: item.url,
              }));
            const added = progress.reduce((sum, item) => sum + item.added, 0);
            this.updateNativeCart(result, after);
            if (!remaining.length) {
              this.completed = true;
              this.input.value = "";
              this.setStatus(
                "Your cart contains the requested quantities. Review your cart before placing your order.",
              );
            } else {
              this.rows = [];
              this.input.value = csvItems(remaining);
              this.stagedItems = remaining;
              this.setStatus(
                `${error.message} ${added ? added + " units are now in your cart. Your list contains only the remaining items." : "Your cart quantities did not increase."} Review the remaining list before trying again.`,
                true,
              );
              // Preserve exact product references, including products without SKUs, for a deliberate fresh review.
            }
          } catch (_) {
            this.uncertain = true;
            this.setStatus(
              "The order request could not be confirmed. To prevent duplicate items, further ordering is paused. Open your cart to check quantities, then refresh this page before starting another list.",
              true,
            );
          }
        }
      } finally {
        this.adding = false;
        this.removeAttribute("aria-busy");
        this.render();
      }
    }
  }

  if (root.customElements && !root.customElements.get("saber-quick-order"))
    root.customElements.define("saber-quick-order", QuickOrder);
  return {
    parseRows,
    normalizeSku,
    resolveRows,
    matchProducts,
    aggregateRows,
    validateAggregate,
    cartQuantities,
    reconcile,
    QuickOrder,
  };
});
